import { memo, useState, useCallback, useEffect, useMemo } from "react";
import {
  Icon,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@repo/ping-design";
import cx from "classnames";
import {
  OverlayCategory,
  OverlayLayer,
  OverlayLayerSelectOptions,
  OverlayLayerSubItem,
  OverlayProvider,
  SpecialComponentProps,
  DisabledCheckProps,
} from "@repo/ping-react-js";
import "./OverlaySelector.scss";

type ViewMode =
  | { type: "providers-layers"; providerId: string | null }
  | { type: "sub-items"; layer: OverlayLayer }
  | { type: "special-component"; layer: OverlayLayer }
  | { type: "provider-special-component"; providerId: string };

type OverlaySelectorProps = {
  category: OverlayCategory;
  onLayerSelect: (
    layer: OverlayLayer | OverlayLayerSubItem,
    options?: OverlayLayerSelectOptions,
  ) => void;
  onClose: () => void;
  specialComponents?: Record<
    string,
    React.ComponentType<SpecialComponentProps>
  >;
  specialComponentProps?: Omit<
    SpecialComponentProps,
    "onLayerSelect" | "onClose"
  >;
  enabledLayer?: OverlayLayer | OverlayLayerSubItem | null;
  // Props for computing disabled state
  disabledCheckProps?: Omit<DisabledCheckProps, "hasUsePermission">;
  availableProviderIds?: string[];
};

const findEnabledLayerInCategory = (
  category: OverlayCategory,
  enabledLayer: OverlayLayer | OverlayLayerSubItem | null | undefined,
): {
  provider: string | null;
  layer: OverlayLayer | null;
  isSubItem: boolean;
} => {
  if (!enabledLayer) {
    return { provider: null, layer: null, isSubItem: false };
  }

  for (const provider of category.providers) {
    // Check if enabledLayer matches this provider (for providers with specialComponent and empty layers)
    if (provider.specialComponent && enabledLayer.id === provider.id) {
      return { provider: provider.id, layer: null, isSubItem: false };
    }

    for (const layer of provider.layers) {
      if (layer.id === enabledLayer.id || layer.name === enabledLayer.name) {
        return { provider: provider.id, layer, isSubItem: false };
      }
      if (layer.subItems) {
        for (const subItem of layer.subItems) {
          if (
            subItem.id === enabledLayer.id ||
            subItem.name === enabledLayer.name
          ) {
            return { provider: provider.id, layer, isSubItem: true };
          }
        }
      }
    }
  }

  return { provider: null, layer: null, isSubItem: false };
};

const OverlaySelector = memo(
  ({
    category,
    onLayerSelect,
    onClose,
    specialComponents,
    specialComponentProps,
    enabledLayer,
    disabledCheckProps,
    availableProviderIds,
  }: OverlaySelectorProps) => {
    const enabledLayerInfo = findEnabledLayerInCategory(category, enabledLayer);

    const getProviderDisabledState = useCallback(
      (
        provider: OverlayProvider,
      ): { isDisabled: boolean; disabledMsg: string | null } => {
        if (!provider.isDisabled || !disabledCheckProps) {
          return { isDisabled: false, disabledMsg: null };
        }

        const hasUsePermission =
          availableProviderIds?.includes(provider.id) ?? false;
        const fullProps: DisabledCheckProps = {
          ...disabledCheckProps,
          hasUsePermission,
        };

        const isDisabled = provider.isDisabled(fullProps);
        const disabledMsg =
          isDisabled && provider.disabledMsg
            ? provider.disabledMsg(fullProps)
            : null;

        return { isDisabled, disabledMsg };
      },
      [disabledCheckProps, availableProviderIds],
    );

    const providerDisabledStates = useMemo(() => {
      const states: Record<
        string,
        { isDisabled: boolean; disabledMsg: string | null }
      > = {};
      for (const provider of category.providers) {
        states[provider.id] = getProviderDisabledState(provider);
      }
      return states;
    }, [category.providers, getProviderDisabledState]);

    const getInitialProvider = (): string | null => {
      if (enabledLayerInfo.provider) {
        return enabledLayerInfo.provider;
      }
      return category.providers.length > 0 ? category.providers[0].id : null;
    };

    const getInitialViewMode = (): ViewMode => {
      const providerId = getInitialProvider();

      if (enabledLayerInfo.provider && enabledLayerInfo.layer) {
        const layer = enabledLayerInfo.layer;

        if (enabledLayerInfo.isSubItem && layer.subItems) {
          return { type: "sub-items", layer };
        }

        if (layer.specialComponent) {
          return { type: "special-component", layer };
        }
      }

      if (providerId) {
        const provider = category.providers.find((p) => p.id === providerId);
        if (provider?.specialComponent) {
          return { type: "provider-special-component", providerId };
        }
      }

      return { type: "providers-layers", providerId };
    };

    const [selectedProvider, setSelectedProvider] = useState<string | null>(
      getInitialProvider,
    );
    const [viewMode, setViewMode] = useState<ViewMode>(getInitialViewMode);

    useEffect(() => {
      const newEnabledLayerInfo = findEnabledLayerInCategory(
        category,
        enabledLayer,
      );

      let newProviderId: string | null;
      if (newEnabledLayerInfo.provider) {
        newProviderId = newEnabledLayerInfo.provider;
        setSelectedProvider(newProviderId);
      } else {
        // Keep current provider when layer doesn't match (e.g., switching to Google Maps)
        newProviderId = selectedProvider ?? (category.providers.length > 0 ? category.providers[0].id : null);
      }

      if (newEnabledLayerInfo.provider && newEnabledLayerInfo.layer) {
        const layer = newEnabledLayerInfo.layer;

        if (newEnabledLayerInfo.isSubItem && layer.subItems) {
          setViewMode({ type: "sub-items", layer });
        } else if (layer.specialComponent) {
          setViewMode({ type: "special-component", layer });
        } else {
          setViewMode({ type: "providers-layers", providerId: newProviderId });
        }
      } else if (newProviderId) {
        const provider = category.providers.find((p) => p.id === newProviderId);
        if (provider?.specialComponent) {
          setViewMode({
            type: "provider-special-component",
            providerId: newProviderId,
          });
        } else {
          setViewMode({
            type: "providers-layers",
            providerId: newProviderId,
          });
        }
      }
    }, [category, enabledLayer]);

    const currentProvider = category.providers.find(
      (p) => p.id === selectedProvider,
    );

    const handleProviderClick = useCallback(
      (providerId: string) => {
        setSelectedProvider(providerId);
        const provider = category.providers.find((p) => p.id === providerId);

        if (provider?.specialComponent) {
          setViewMode({ type: "provider-special-component", providerId });
        } else {
          setViewMode({ type: "providers-layers", providerId });
        }
      },
      [category],
    );

    const handleLayerClick = useCallback(
      (layer: OverlayLayer) => {
        if (layer.specialComponent) {
          setViewMode({ type: "special-component", layer });
        } else if (layer.subItems && layer.subItems.length > 0) {
          setViewMode({ type: "sub-items", layer });
        } else {
          onLayerSelect(layer, {
            zoomIntoBuilding: layer.zoomIntoBuilding,
          });
          onClose();
        }
      },
      [onLayerSelect, onClose],
    );

    const handleSubItemClick = useCallback(
      (subItem: OverlayLayerSubItem) => {
        onLayerSelect(subItem, {
          zoomIntoBuilding: subItem.zoomIntoBuilding,
        });
        onClose();
      },
      [onLayerSelect, onClose],
    );

    const handleBack = useCallback(() => {
      setViewMode({
        type: "providers-layers",
        providerId: selectedProvider,
      });
    }, [selectedProvider]);

    if (category.providers.length === 0) {
      return (
        <div className="OverlaySelector__NoProviders">
          <p>No providers available</p>
        </div>
      );
    }

    if (viewMode.type === "providers-layers") {
      return (
        <div className="OverlaySelector">
          <div className="OverlaySelector__ProvidersPane">
            <div className="OverlaySelector__ProvidersList">
              {category.providers.map((provider) => {
                const { isDisabled, disabledMsg } = providerDisabledStates[
                  provider.id
                ] || { isDisabled: false, disabledMsg: null };

                const buttonContent = (
                  <button
                    key={provider.id}
                    className={cx("OverlaySelector__ProviderItem", {
                      "OverlaySelector__ProviderItem--Active":
                        selectedProvider === provider.id,
                      "OverlaySelector__ProviderItem--Disabled": isDisabled,
                    })}
                    onClick={() =>
                      !isDisabled && handleProviderClick(provider.id)
                    }
                    disabled={isDisabled}
                  >
                    <span>{provider.label}</span>
                    {isDisabled && (
                      <Icon
                        iconName="Lock"
                        size={12}
                        strokeWidth={2.5}
                        className="OverlaySelector__LockIcon"
                      />
                    )}
                    {!isDisabled && (
                      <Icon
                        iconName="ChevronRight"
                        size={20}
                        strokeWidth={1.5}
                      />
                    )}
                  </button>
                );

                if (isDisabled && disabledMsg) {
                  return (
                    <Tooltip key={provider.id} placement="right">
                      <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
                      <TooltipContent>
                        {disabledMsg}
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return buttonContent;
              })}
            </div>
          </div>
          <div className="OverlaySelector__LayersPane">
            <div className="OverlaySelector__LayersList">
              {currentProvider?.layers.map((layer) => (
                <button
                  key={layer.id}
                  className="OverlaySelector__LayerItem"
                  onClick={() => handleLayerClick(layer)}
                >
                  <span>{layer.label}</span>
                  {(layer.subItems || layer.specialComponent) && (
                    <Icon iconName="ChevronRight" size={16} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (viewMode.type === "sub-items" && "layer" in viewMode) {
      const layer = viewMode.layer;
      return (
        <div className="OverlaySelector OverlaySelector--SubItems">
          <div className="OverlaySelector__SubItemsHeaderContainer">
            <button
              className="OverlaySelector__BackButton"
              onClick={handleBack}
            >
              <Icon iconName="ArrowLeft" size={16} />
            </button>
            <div className="OverlaySelector__SubItemsHeader">{layer.label}</div>
          </div>
          <div className="OverlaySelector__SubItemsList">
            {layer.subItems?.map((subItem) => (
              <button
                key={subItem.id}
                className="OverlaySelector__SubItem"
                onClick={() => handleSubItemClick(subItem)}
              >
                <span>{subItem.label}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (viewMode.type === "special-component" && "layer" in viewMode) {
      const layer = viewMode.layer;
      const SpecialComponent =
        layer.specialComponent && specialComponents?.[layer.specialComponent];

      return (
        <div className="OverlaySelector OverlaySelector--SpecialComponent">
          <div className="OverlaySelector__SubItemsHeaderContainer">
            <button
              className="OverlaySelector__BackButton"
              onClick={handleBack}
            >
              <Icon iconName="ArrowLeft" size={16} />
            </button>
            <div className="OverlaySelector__SubItemsHeader">{layer.label}</div>
          </div>
          <div className="OverlaySelector__SpecialComponentContainer">
            {SpecialComponent ? (
              <SpecialComponent
                onLayerSelect={(
                  layer: OverlayLayer | OverlayLayerSubItem,
                  options?: OverlayLayerSelectOptions,
                ) => {
                  onLayerSelect(layer, options);
                }}
                onClose={onClose}
                {...specialComponentProps}
              />
            ) : (
              <div className="OverlaySelector__Placeholder">
                <h3>{layer.label}</h3>
                <p>Special component: {layer.specialComponent}</p>
                <p>
                  Component not found. Please implement {layer.specialComponent}
                  .
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (
      viewMode.type === "provider-special-component" &&
      "providerId" in viewMode
    ) {
      const provider = category.providers.find(
        (p) => p.id === viewMode.providerId,
      );
      const SpecialComponent =
        provider?.specialComponent &&
        specialComponents?.[provider.specialComponent];

      return (
        <div className="OverlaySelector">
          <div className="OverlaySelector__ProvidersPane">
            <div className="OverlaySelector__ProvidersList">
              {category.providers.map((prov) => {
                const { isDisabled, disabledMsg } = providerDisabledStates[
                  prov.id
                ] || { isDisabled: false, disabledMsg: null };

                const buttonContent = (
                  <button
                    key={prov.id}
                    className={cx("OverlaySelector__ProviderItem", {
                      "OverlaySelector__ProviderItem--Active":
                        selectedProvider === prov.id,
                      "OverlaySelector__ProviderItem--Disabled": isDisabled,
                    })}
                    onClick={() => !isDisabled && handleProviderClick(prov.id)}
                    disabled={isDisabled}
                  >
                    <span>{prov.label}</span>
                    {isDisabled && (
                      <Icon
                        iconName="Lock"
                        size={12}
                        strokeWidth={2.5}
                        className="OverlaySelector__LockIcon"
                      />
                    )}
                    {!isDisabled && (
                      <Icon
                        iconName="ChevronRight"
                        size={20}
                        strokeWidth={1.5}
                      />
                    )}
                  </button>
                );

                if (isDisabled && disabledMsg) {
                  return (
                    <Tooltip key={prov.id} placement="right">
                      <TooltipTrigger asChild>{buttonContent}</TooltipTrigger>
                      <TooltipContent>
                        {disabledMsg}
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return buttonContent;
              })}
            </div>
          </div>
          <div className="OverlaySelector__LayersPane">
            <div className="OverlaySelector__SpecialComponentContainer">
              {SpecialComponent ? (
                <SpecialComponent
                  onLayerSelect={(
                    layer: OverlayLayer | OverlayLayerSubItem,
                    options?: OverlayLayerSelectOptions,
                  ) => {
                    onLayerSelect(layer, options);
                  }}
                  onClose={onClose}
                  {...specialComponentProps}
                />
              ) : (
                <div className="OverlaySelector__Placeholder">
                  <h3>{provider?.label}</h3>
                  <p>Special component: {provider?.specialComponent}</p>
                  <p>
                    Component not found. Please implement{" "}
                    {provider?.specialComponent}.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return null;
  },
);

OverlaySelector.displayName = "OverlaySelector";

export default OverlaySelector;
